/*
 * The router is built once, at boot, because a module's routes and its translations arrive with it:
 * `registerModules` says which modules exist, each one hands over its route bundle and its
 * namespace, and only then is there a tree to navigate. That is also why this is async — the shell
 * mounts after the answer, not before.
 *
 * Both halves may be chunks of their own (chat's are), and every one of them is asked for at once:
 * awaiting a module's routes before asking for its copy would put a second round trip in front of
 * the first paint. `main.tsx` asks for the session beside all of this, so on a cold load the chunks
 * cost the longer of the two requests rather than their sum. The copy is registered before the
 * router exists, so no screen ever renders a key without its text.
 */

import {
	createRoute,
	createRouter,
	Outlet,
	type AnyRoute,
	type RouterHistory,
} from "@tanstack/react-router"
import { addCatalogue } from "../core/i18n/index.ts"
import { fullPagesOf, type ModuleId, type ModuleRegistry } from "../core/modules/registry.ts"
import { moduleActive } from "../core/modules/slots.ts"
import { useModel, useShell } from "../core/shell/context.tsx"
import { NotFoundPage } from "./frames.tsx"
import {
	authRoutes,
	coreRoutes,
	fullPageRoutes,
	NotFoundScreen,
	rootRoute,
	shellRoute,
} from "./routes.tsx"

export interface AppRouterOptions {
	/** A memory history, for a test that mounts the application without a browser URL. */
	history?: RouterHistory
}

/**
 * A module's pages, drawn only for a reader who may use the module: the instance runs it and the
 * module admits them — `moduleActive`, the rule the rail and every slot already follow. Anybody else
 * gets the page an address that leads nowhere gets, because for them it does: a guest at `/ai`, or a
 * team member at the address of a module this instance does not run, would otherwise be drawn a
 * module's screens whose every request is refused. The gate stands inside the shell route, so it
 * draws the page alone, as the shell's own `notFoundComponent` does: `NotFoundScreen` would frame a
 * second shell inside the first.
 */
function ModuleGate({ id }: { id: ModuleId }) {
	const { registry } = useShell()
	const active = useModel((model) => moduleActive(registry, id, model))
	return active ? <Outlet /> : <NotFoundPage />
}

export async function createAppRouter(registry: ModuleRegistry, options: AppRouterOptions = {}) {
	const modules = registry.all()
	const bundles = await Promise.all(
		modules.map(async (mod) => {
			// Each module's routes hang under a pathless route of its own, which is the gate above.
			const gate = createRoute({
				getParentRoute: () => shellRoute,
				id: `module-${mod.id}`,
				component: () => <ModuleGate id={mod.id} />,
			})
			const [routes, i18n] = await Promise.all([mod.routes(gate), mod.i18n()])
			return { id: mod.id, routes: [gate.addChildren(routes)], i18n }
		}),
	)
	for (const bundle of bundles) addCatalogue(bundle.id, bundle.i18n)

	const children: AnyRoute[] = [...coreRoutes, ...bundles.flatMap((bundle) => bundle.routes)]
	// A module's full pages hang off the root beside the sign-in screens: no shell around them.
	const routeTree = rootRoute.addChildren([
		...authRoutes,
		...fullPageRoutes(fullPagesOf(registry)),
		shellRoute.addChildren(children),
	])

	return createRouter({
		routeTree,
		defaultNotFoundComponent: NotFoundScreen,
		...(options.history === undefined ? {} : { history: options.history }),
	})
}

export type AppRouter = Awaited<ReturnType<typeof createAppRouter>>
